import { httpDelete, httpGet, httpPostFormData } from "shared/api/httpClient";
import { catching } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { z } from "zod";

function intoFormData(file: File): FormData {
    const fd = new FormData();
    fd.append("_data", file);
    return fd;
}

/* Represents an uploaded document object, as it exists on the API  */
const fileObjectSchema = z.object({
    id: z.string(),
    fileDescription: z.string(),
    fileName: z.string(),
    fileSize: z.number(),
    fileType: z.string(),
});
export type FileObject = z.infer<typeof fileObjectSchema>;

const signedGrantSchema = z.object({
    presignedUrl: z.string().transform((token) => `${import.meta.env["VITE_API_BASE_URL"]}/presignedUrl/${token}`),
    fileObjectId: z.string(),
    ok: z.boolean().default(true),
});
type SignedGrantSchema = z.infer<typeof signedGrantSchema>;

export const fileApi = {
    async upload(file: File, opts?: { usage?: "general" | "ipaws" }): Promise<FileObject> {
        return await httpPostFormData(`/document?usage=${(opts?.usage ?? "general").toUpperCase()}`, intoFormData(file));
    },

    async get(id: string): Promise<FileObject> {
        return await httpGet(`/document/${id}`).then(fileObjectSchema.parse);
    },

    async sign(id: string): Promise<SignedGrantSchema> {
        const grant = await httpGet(`/document/${id}/data`).then(signedGrantSchema.parse);

        function delay(ms: number) {
            return new Promise<void>((resolve) => setTimeout(resolve, ms));
        }

        async function verify(attempt: number = 0) {
            if ((await catching(() => fetch(grant.presignedUrl)))?.ok) return grant;

            if (attempt > 3) {
                logger.warn(`Invalid presignedUrl '${grant.presignedUrl}' for ${id}.`);
                return Promise.resolve<SignedGrantSchema>({
                    fileObjectId: id,
                    presignedUrl: "/no-file.png",
                    ok: false,
                });
            }

            logger.log("Grant presignedUrl not ready yet. retrying...");
            await delay(3 ** attempt * 100);
            return await verify(attempt + 1);
        }

        return verify();
    },

    async delete(id: string): Promise<void> {
        return httpDelete(`/document/${id}`);
    },
};
